import React, { useState, useRef, MouseEventHandler } from 'react';
import { Popup } from 'react-leaflet';
import moment from 'moment';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Popper from '@mui/material/Popper';
import Battery6BarIcon from '@mui/icons-material/Battery6Bar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import NavigationIcon from '@mui/icons-material/Navigation';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SimCardIcon from '@mui/icons-material/SimCard';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CellTowerIcon from '@mui/icons-material/CellTower';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useDictionary from '@ui/hooks/useDictionary';
import { IMarkerProps } from './IMarkerProps';

interface IState {
    coordsMsgVisible: boolean;
    coordsEl: HTMLElement | null;
}

const Component: React.FC<IMarkerProps> = ({
    gpsCoord,
    lbsCoord,
    imei,
    name,
    date,
    gpsDate,
    lbsDate,
    timezone,
    speed,
    dir,
    batteryVoltage,
    event,
    temperature,
    cur_sim,
    signal,
}: IMarkerProps) => {
    const getWord = useDictionary();
    let position = [0, 0];
    let coordsDate = getWord('noData');
    let coordType = getWord('noData');
    if (gpsCoord && gpsCoord.lat && gpsCoord.lon) {
        position = [gpsCoord.lat, gpsCoord.lon];
        coordType = getWord('gps');
        coordsDate = moment(gpsDate)
            .utcOffset(timezone)
            .format('HH:mm DD.MM.YYYY');
    } else if (lbsCoord && lbsCoord.lat && lbsCoord.lon) {
        position = [lbsCoord.lat, lbsCoord.lon];
        coordType = getWord('lbs');
        coordsDate = moment(lbsDate)
            .utcOffset(timezone)
            .format('HH:mm DD.MM.YYYY');
    }
    const [lat, lon] = position.map((v) => v.toFixed(6));
    const coords = `${lat}, ${lon}`;
    const dateStr = date
        ? moment(date).utcOffset(timezone).format('HH:mm DD.MM.YYYY')
        : getWord('noData');
    const isValidCourse = typeof dir === 'number';
    const courseVal = isValidCourse ? dir : 0;
    const speedStr =
        typeof speed === 'number'
            ? `${speed} ${getWord('km/h')}`
            : getWord('noData');
    const courseStr = isValidCourse
        ? `${dir}${getWord('°')}`
        : getWord('noData');
    // const batteryStr =
    //     typeof batteryVoltage === 'number'
    //         ? `${batteryVoltage.toFixed(2)} ${getWord('V')}`
    //         : getWord('noData');  
    const batteryStr =
        Number.isInteger(batteryVoltage)
            ? `${batteryVoltage} %`
            : typeof batteryVoltage === 'number'
                ? `${batteryVoltage.toFixed(2)} ${getWord('V')}`
                : getWord('noData')
    const eventStr = event ? `${getWord(event)}` : getWord('noData');
    const temperatureStr = temperature ? `${temperature}°C` : getWord('noData');
    const curSimStr = typeof cur_sim === 'string' ? cur_sim : getWord('noData');
    const signalStr = signal ? signal : getWord('noData');
    const coordsRef = useRef(null);
    const [state, setState] = useState<IState>({
        coordsMsgVisible: false,
        coordsEl: null,
    });
    const handleCoordsClick: MouseEventHandler<HTMLElement> = async (e) => {
        const target = e.target as HTMLElement;
        const coords = target.innerText;
        await navigator.clipboard.writeText(coords);
        setState((prev) => ({
            ...prev,
            coordsMsgVisible: true,
            coordsEl: coordsRef.current,
        }));
        setTimeout(() => {
            setState((prev) => ({
                ...prev,
                coordsMsgVisible: false,
            }));
        }, 3000);
    };
    const gpsCoordData = [
        {
            title: getWord('coordTime'),
            value: coordsDate,
            Icon: AccessTimeIcon,
        },
        {
            title: getWord('coords'),
            value: coords,
            Icon: LocationOnIcon,
        },
    ];
    const lbsCoordData = [
        {
            title: getWord('coordType'),
            value: coordType,
            Icon: MyLocationIcon,
        },
        {
            title: getWord('simCard'),
            value: curSimStr,
            Icon: SimCardIcon,
        },
        {
            title: getWord('signal'),
            value: signalStr,
            Icon: CellTowerIcon,
        },
    ];
    const teleData = [
        {
            title: getWord('telemetryTime'),
            value: dateStr,
            Icon: AccessTimeIcon,
        },
        {
            title: getWord('event'),
            value: eventStr,
            Icon: InfoIcon,
        },
        {
            title: getWord('temperature'),
            value: temperatureStr,
            Icon: ThermostatIcon,
        },
        {
            title: getWord('batteryVoltage'),
            value: batteryStr,
            Icon: Battery6BarIcon,
        },
        {
            title: getWord('speed'),
            value: speedStr,
            Icon: SpeedIcon,
        },
        {
            title: getWord('dir'),
            value: courseStr,
            Icon: NavigationIcon,
        },
    ];
    const teleItems = teleData.map(({ title, value, Icon }) => (
        <ListItem className="marker-popup__item" key={title}>
            <ListItemAvatar
                className="marker-popup__item-avatar"
                sx={{ minWidth: '0px' }}
            >
                <Avatar className="marker-popup__avatar">
                    <Icon className="marker-popup__icon" />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                classes={{
                    root: 'marker-popup__item-text',
                    primary: 'marker-popup__text_primary',
                    secondary: 'marker-popup__text_secondary',
                }}
                primary={title}
                secondary={value}
            />
        </ListItem>
    ));

    const gpsCoordItems = gpsCoordData.map(({ title, value, Icon }) => (
        <ListItem
            className="marker-popup__item"
            ref={title === getWord('coords') ? coordsRef : undefined}
            key={title}
        >
            <ListItemAvatar
                className="marker-popup__item-avatar"
                sx={{ minWidth: '0px' }}
            >
                <Avatar className="marker-popup__avatar">
                    <Icon className="marker-popup__icon" />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                classes={{
                    root: 'marker-popup__item-text',
                    primary: 'marker-popup__text_primary',
                    secondary:
                        title === getWord('coords')
                            ? 'marker-popup__text_secondary_active'
                            : 'marker-popup__text_secondary',
                }}
                primary={title}
                secondary={value}
                onClick={
                    title === getWord('coords') ? handleCoordsClick : undefined
                }
            />
            <Popper
                className="marker-popup__popper"
                placement="bottom-start"
                open={state.coordsMsgVisible}
                anchorEl={state.coordsEl}
                container={
                    state.coordsEl
                        ? state.coordsEl.parentElement
                        : document.body
                }
                modifiers={[{ name: 'flip', enabled: false }]}
            >
                {getWord('copied')}
            </Popper>
        </ListItem>
    ));
    const lbsCoordItems = lbsCoordData.map(({ title, value, Icon }) => (
        <ListItem className="marker-popup__item" key={title}>
            <ListItemAvatar
                className="marker-popup__item-avatar"
                sx={{ minWidth: '0px' }}
            >
                <Avatar className="marker-popup__avatar">
                    <Icon className="marker-popup__icon" />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                classes={{
                    root: 'marker-popup__item-text',
                    primary: 'marker-popup__text_primary',
                    secondary: 'marker-popup__text_secondary',
                }}
                primary={title}
                secondary={value}
            />
        </ListItem>
    ));

    return (
        <Popup className="marker-popup" autoPan={false}>
            <Typography className="marker-popup__title">{name}</Typography>
            <Typography className="marker-popup__text">
                <Typography
                    className="marker-popup__text_imei"
                    component="span"
                >
                    IMEI:
                </Typography>
                {` ${imei}`}
            </Typography>
            <Divider />
            <Box className="marker-popup__list-wrap">
                <Box>
                    <List className="marker-popup__list">{teleItems}</List>
                </Box>
                <Box>
                    <List className="marker-popup__list">
                        {gpsCoordItems}
                        {lbsCoordItems}
                    </List>
                </Box>
            </Box>
        </Popup>
    );
};

const MarkerPopupComponent = React.memo(Component);

export default MarkerPopupComponent;
